import pino from 'pino';
import { ErrorInfo } from 'react';

/**
 * Edge runtime (middleware, edge route handlers) has no `window` but also lacks the full Node
 * APIs pino uses for stdout. Pino therefore falls back to its browser build in edge, where
 * `console.log(object)` prints a multi-line dump that GCP Cloud Logging splits into separate
 * `textPayload` entries (see middleware.ts header-info calls). Detecting edge lets us force
 * single-line JSON via `browser.write` instead. Node SSR/RSC and the real browser are unaffected.
 */
const isEdgeRuntime = typeof process !== 'undefined' && process.env.NEXT_RUNTIME === 'edge';

/**
 * @description Logger instance for both client and server side.
 * ** Note: Client side uses browser property to send the request using method `navigator.sendBeacon` **
 * ** Note: Server side don't require any configuration **
 *
 * @example Client side
 * useEffect(() => {
 *  logger.info('client side hello');
 * }, []);
 *
 * @example
 * Server side
 * import { logger } from '@utils/logger/logger';
 * getServerSideProps() {
 * ....
 * logger.info('server side hello');
 * ...
 * }
 */
export const logger = pino({
  name: 'bj-react',
  level: 'info',
  // show severity field as text instead of number
  formatters: {
    level: (label) => ({ severity: label.toUpperCase() }),
  },
  // show timestamp in ISO format
  timestamp: pino.stdTimeFunctions.isoTime,
  // client side logger
  browser: {
    asObject: true,
    ...(isEdgeRuntime
      ? {
          // Edge: stringify the log object so it stays on a single line and Cloud Logging keeps it
          // as one structured entry. We inject `severity` (browser mode skips the `formatters.level`
          // above) so edge logs match the server-side severity mapping, and route each level to the
          // matching console method to preserve stdout/stderr separation.
          /* eslint-disable no-console -- in edge runtime pino has no stdout transport; console is the only sink */
          write: {
            fatal: (o: object) => console.error(JSON.stringify({ severity: 'CRITICAL', ...o })),
            error: (o: object) => console.error(JSON.stringify({ severity: 'ERROR', ...o })),
            warn: (o: object) => console.warn(JSON.stringify({ severity: 'WARNING', ...o })),
            info: (o: object) => console.log(JSON.stringify({ severity: 'INFO', ...o })),
            debug: (o: object) => console.log(JSON.stringify({ severity: 'DEBUG', ...o })),
            trace: (o: object) => console.log(JSON.stringify({ severity: 'DEBUG', ...o })),
          },
          /* eslint-enable no-console */
        }
      : {
          transmit: {
            level: 'info',
            send: (level, logEvent) => {
              // max beacon payload size is 2^16, but we need to leave some space for headers and level field
              const msg = (logEvent.messages[0] as string).slice(0, 65536 - 100);

              // ignore error messages from CSP from chrome extensions
              if (
                level === 'error' &&
                msg.toLowerCase().includes('is not an allowed source of script in the following content security policy directive') &&
                msg.toLowerCase().includes('chrome-extension://')
              ) {
                return;
              }

              // send log to server
              const headers = {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
                type: 'application/json',
              };
              const blob = new Blob([JSON.stringify({ msg, level })], headers);
              // use navigator.sendBeacon for sending data because is better for sending analytics data than xhr/fetch
              navigator.sendBeacon('/api/log-client-side', blob);
            },
          },
        }),
  },
});

/**
 * @description Logs event detected by the document (client-side).
 * @param errorEvent
 */
export const logErrorEvent = (errorEvent: ErrorEvent) => {
  // default error message
  let errorMessage = errorEvent.message;

  const error = errorEvent?.error as { message: string; stack: string };
  // logg error message if available
  if (error?.message !== undefined) {
    errorMessage = `${error.message}. `;
  }
  // logg stack if available
  if (error?.stack !== undefined) {
    errorMessage = error.stack;
  }

  if (typeof window !== 'undefined') {
    const { userAgent } = window.navigator;
    logger.error(`[WindowError] in page ${window.location.href}\n User agent: ${userAgent}\n ${errorMessage}`);
  }
};

/**
 * @description Logs error info detected by the ErrorBoundary (client-side).
 * @param error
 * @param errorInfo
 */
export const logErrorBoundary = (error: Error, errorInfo: ErrorInfo) => {
  let message = error?.stack ?? '';
  if (errorInfo?.componentStack?.length) {
    message = `${error.name}: ${error.message} ${errorInfo.componentStack}`;
  }

  const { userAgent } = window.navigator;
  logger.error(`[ErrorBoundary] in page: ${window.location.href}\n User agent: ${userAgent}\n ${message}`);
};
